"use client";

import { IconCaretDown, IconCaretUp } from "@decibeltrade/react-icons";
import { forwardRef } from "react";
import { css, cva, cx } from "styled-system/css";

import { Clickable } from "./clickable";

interface CollapsibleIconButtonProps {
  collapsed: boolean;
  onClick?: () => void;
  ariaLabel: string;
  className?: string;
}

export const CollapsibleIconButton = forwardRef<HTMLButtonElement, CollapsibleIconButtonProps>(
  ({ collapsed, onClick, ariaLabel, className }, ref) => {
    return (
      <Clickable
        ref={ref}
        onClick={onClick}
        ariaLabel={ariaLabel}
        aria-expanded={!collapsed}
        className={cx(buttonStyles({ collapsed }), className)}
      >
        <IconCaretUp className={iconStyles} />
        <IconCaretDown className={iconStyles} />
      </Clickable>
    );
  },
);
CollapsibleIconButton.displayName = "CollapsibleIconButton";

const buttonStyles = cva({
  base: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    w: "32",
    h: "32",
    borderRadius: "100",
    color: "text.secondary",
    cursor: "pointer",
    bg: "transparent",
    border: "none",
    gap: "0",
    transitionPreset: "colors",

    "& svg": {
      transition: "all",
      transitionDuration: "100",
      transitionTimingFunction: "ease-in-out",
    },
    _hover: {
      color: "text.primary",
    },
  },
  variants: {
    collapsed: {
      true: {
        // When collapsed, rotate 180deg and add small spacing
        "& svg:first-child": {
          transform: "rotate(180deg) translateY(-1px)",
        },
        "& svg:last-child": {
          transform: "rotate(180deg) translateY(1px)",
        },
        _hover: {
          // On hover, icons move closer together
          "& svg:first-child": {
            transform: "rotate(180deg) translateY(1px)",
          },
          "& svg:last-child": {
            transform: "rotate(180deg) translateY(-1px)",
          },
        },
      },
      false: {
        // When expanded, no rotation, just spacing
        "& svg:first-child": {
          transform: "translateY(2px)",
        },
        "& svg:last-child": {
          transform: "translateY(-2px)",
        },
        _hover: {
          // On hover, icons move farther apart
          "& svg:first-child": {
            transform: "translateY(3px)",
          },
          "& svg:last-child": {
            transform: "translateY(-3px)",
          },
        },
      },
    },
  },
});

const iconStyles = css({
  w: "12",
  h: "12",
});
